import { RAW_UNDERGRADUATE_INSTITUTIONS } from './undergraduateInstitutions.data';

export type UndergraduateInstitution = {
  name: string;
  domain: string;
};

function normalizeDomain(domain: string): string {
  return domain.trim().toLowerCase().replace(/^www\./, '');
}

function buildInstitutions(): UndergraduateInstitution[] {
  const seen = new Set<string>();
  const out: UndergraduateInstitution[] = [];
  for (const row of RAW_UNDERGRADUATE_INSTITUTIONS) {
    const name = row.name.trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    const domain = normalizeDomain(row.domain);
    if (!domain) continue;
    out.push({ name, domain });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/** Deduplicated by display name, sorted A–Z. Logos are remote favicons; missing icons still show the row. */
export const UNDERGRADUATE_INSTITUTIONS: UndergraduateInstitution[] = buildInstitutions();

/**
 * Empty search: show only this list (ranked), capped at `limit` — type to search the full catalog.
 * Order: founder block, Ivies, then ~U.S. News national universities tier (extended).
 */
const FOUNDER_PRIORITY_NAMES = [
  'The Ohio State University',
  'Harvard University',
  'Stanford University',
  'Massachusetts Institute of Technology',
  'Princeton University',
  'Yale University',
  'University of Pennsylvania',
  'Columbia University',
  'Brown University',
  'Cornell University',
  'Dartmouth College',
  'California Institute of Technology',
  'Duke University',
  'Johns Hopkins University',
  'Northwestern University',
  'University of Chicago',
  'University of California, Los Angeles',
  'University of California, Berkeley',
  'Rice University',
  'University of Notre Dame',
  'Vanderbilt University',
  'Washington University in St. Louis',
  'Carnegie Mellon University',
  'University of Michigan',
  'Emory University',
  'Georgetown University',
  'University of Virginia',
  'University of North Carolina at Chapel Hill',
  'University of Southern California',
  'New York University',
  'University of Florida',
  'The University of Texas at Austin',
  'Georgia Institute of Technology',
  'University of Illinois Urbana-Champaign',
  'University of Washington',
  'Tufts University',
  'University of California, San Diego',
  'Boston College',
  'Wake Forest University',
  'University of Wisconsin–Madison',
  'William & Mary',
  'University of Rochester',
  'Case Western Reserve University',
  'University of California, Davis',
  'University of California, Irvine',
  'University of California, Santa Barbara',
  'Purdue University',
  'Texas A&M University',
  'Northeastern University',
  'Rutgers University',
  'University of Maryland, College Park',
  'University of Miami',
  'Tulane University',
  'University of Georgia',
  'University of Pittsburgh',
  'Syracuse University',
  'University of Connecticut',
  'Clemson University',
  'Virginia Tech',
  'Pennsylvania State University',
  'Indiana University Bloomington',
  'Michigan State University',
  'Florida State University',
  'Baylor University',
  'Southern Methodist University',
  'Texas Christian University',
  'Brigham Young University',
  'George Washington University',
  'American University',
  'Fordham University',
  'Stevens Institute of Technology',
  'Illinois Institute of Technology',
  'Lehigh University',
  'Villanova University',
  'Marquette University',
  'University of Denver',
  'Loyola Marymount University',
  'Pepperdine University',
  'Santa Clara University',
  'University of San Diego',
  'University of Dayton',
  'Chapman University',
  'Howard University',
  'Creighton University',
  'Arizona State University',
  'University of Arizona',
  'University of Colorado Boulder',
  'University of Oregon',
  'University of Kansas',
  'University of Kentucky',
  'University of Iowa',
  'University of Minnesota Twin Cities',
  'The University of Alabama',
  'Auburn University',
  'University of South Carolina',
  'University of Tennessee, Knoxville',
  'University of Missouri',
  'University of Nebraska–Lincoln',
  'University of Oklahoma',
  'Louisiana State University'
] as const;

const FOUNDER_PINNED_ORDER: UndergraduateInstitution[] = FOUNDER_PRIORITY_NAMES.flatMap((name) => {
  const found = UNDERGRADUATE_INSTITUTIONS.find((i) => i.name.toLowerCase() === name.toLowerCase());
  return found ? [found] : [];
});

export function findInstitutionByName(name: string): UndergraduateInstitution | undefined {
  const n = name.trim().toLowerCase();
  if (!n) return undefined;
  return UNDERGRADUATE_INSTITUTIONS.find((i) => i.name.toLowerCase() === n);
}

export function filterInstitutions(query: string, limit = 80): UndergraduateInstitution[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return FOUNDER_PINNED_ORDER.slice(0, limit);
  }
  const matches = UNDERGRADUATE_INSTITUTIONS.filter((i) => i.name.toLowerCase().includes(q));
  matches.sort((a, b) => {
    const al = a.name.toLowerCase();
    const bl = b.name.toLowerCase();
    const aStarts = al.startsWith(q) ? 0 : 1;
    const bStarts = bl.startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    const ai = al.indexOf(q);
    const bi = bl.indexOf(q);
    if (ai !== bi) return ai - bi;
    return a.name.localeCompare(b.name);
  });
  return matches.slice(0, limit);
}
